import React from "react";
import { DayNumber } from "@/types";
import { getShortDayName } from "@/utils/shiftFormUtils";

interface DayGroupSelectorProps {
  selectedDays: DayNumber[];
  availableDays: DayNumber[];
  onChange: (days: DayNumber[]) => void;
  disabled?: boolean;
  className?: string;
}

export function DayGroupSelector({
  selectedDays,
  availableDays,
  onChange,
  disabled = false,
  className = ""
}: DayGroupSelectorProps) {
  const allDays: DayNumber[] = [1, 2, 3, 4, 5, 6, 7]; // Sun-Sat (1-based)

  const toggleDay = (day: DayNumber) => {
    if (disabled) return;
    
    if (selectedDays.includes(day)) {
      // Remove day
      onChange(selectedDays.filter(d => d !== day));
    } else {
      // Add day (only if available)
      if (availableDays.includes(day)) {
        onChange([...selectedDays, day].sort());
      }
    }
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {allDays.map((day) => {
        const isSelected = selectedDays.includes(day);
        const isAvailable = availableDays.includes(day);
        const isDisabled = disabled || (!isSelected && !isAvailable);

        return (
          <button
            key={day}
            type="button"
            onClick={() => toggleDay(day)}
            disabled={isDisabled}
            className={`
              w-8 h-8 rounded-md text-xs font-medium transition-colors
              ${isSelected
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : isDisabled
                ? "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                : "bg-muted text-muted-foreground hover:bg-muted/80 border border-border"
              }
            `}
            title={`${getShortDayName(day)} ${isSelected ? 'selected' : isDisabled ? 'unavailable' : 'available'}`}
            aria-label={`${getShortDayName(day)} ${isSelected ? 'selected' : 'unselected'}`}
          >
            {getShortDayName(day).substring(0, 2)}
          </button>
        );
      })}
    </div>
  );
}
